import { cn } from "@-/com.npmjs.cn";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { keepPreviousData, useQuery } from "@-/com.npmjs.tanstack__react-query";
import { Progress } from "@-/dev.improve.haniverse.components.ui.progress";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useDailyNewGraphemeAllowanceQueryOptions } from "@-/dev.improve.haniverse.lib.learn.new-grapheme-limit";
import { useIsNavAccountDrawerOpen } from "#nav-account-drawer-context";

/**
 * Today's new cards against the learner's Daily New Cards limit, in the profile surface's body:
 * "Cards learned today" with a bar and `x/limit`. Without a limit (No Limit) there is nothing to
 * fill toward, so the row keeps its height and shows the count alone. Tapping it opens
 * Settings → Daily New Cards, where the limit is set.
 *
 * Counted per character, like the limit itself (`useDailyNewGraphemeAllowanceQueryOptions`): a new
 * character's meaning and pronunciation cards are one new card. Read only while the drawer is open,
 * like the wallet; a closed drawer keeps the last count on screen.
 */
export function NavAccountDrawerCardsLearnedToday({
  onOpenDailyNewCards,
}: {
  onOpenDailyNewCards: () => void;
}) {
  const isOpen = useIsNavAccountDrawerOpen();
  const { data: allowance } = useQuery({
    ...useDailyNewGraphemeAllowanceQueryOptions(),
    enabled: isOpen,
    placeholderData: keepPreviousData,
    throwOnError: false,
  });
  // Reserve the row before the first read so the profile surface never shifts.
  const isLoaded = allowance !== undefined;
  const introducedTodayCount = allowance?.introducedTodayCount ?? 0;
  const limit = allowance?.limit ?? Number.POSITIVE_INFINITY;
  const hasLimit = Number.isFinite(limit);
  const count = introducedTodayCount.toLocaleString();

  return (
    <Pressable
      accessibilityElementsHidden={!isLoaded}
      accessibilityHint="Opens Daily New Cards settings"
      accessibilityLabel={
        hasLimit
          ? `${count} of ${limit.toLocaleString()} new cards learned today`
          : `${count} new cards learned today, no daily limit`
      }
      accessibilityRole="button"
      className={cn(
        "flex-row items-center gap-3 rounded-xs active:bg-accent",
        !isLoaded && "opacity-0",
      )}
      disabled={!isLoaded}
      hitSlop={8}
      onPress={onOpenDailyNewCards}
      testID="nav-profile-drawer-cards-learned-today"
    >
      <Text className="shrink-0 text-xs text-muted-foreground">Cards learned today</Text>
      <View className="min-w-0 flex-1">
        {hasLimit ? (
          <Progress
            className="h-1.5 bg-primary/15"
            indicatorClassName="bg-primary"
            value={Math.min(100, (100 * introducedTodayCount) / limit)}
          />
        ) : null}
      </View>
      <Text
        className="shrink-0 text-xs font-semibold tabular-nums"
        testID="nav-profile-drawer-cards-learned-today-count"
      >
        {hasLimit ? `${count}/${limit.toLocaleString()}` : count}
      </Text>
    </Pressable>
  );
}
